import { useState } from "react";
import { useAtomValue } from "jotai";
import { FundAtom } from "../../../store";
import { Fund } from "../../../types/Fund";
import { addOrUpdateFund, deleteFundById } from "../../../api";
import { toast } from "sonner";
import { ManageFundsProps } from "./ManageFunds";

interface UseManageFundsController {
  open: boolean;
  setOpen: (open: boolean) => void;
  funds: Fund[];
  createNewFund: (name: string) => void;
  editingFundId?: number;
  setEditingFundId: (id?: number) => void;
  updateFund: (fund: Fund, name: string) => void;
  deleteFund: (fundId: number) => void;
}

export const useManageFundsController = (
  props: ManageFundsProps
): UseManageFundsController => {
  const [open, setOpen] = useState(false);
  const [editingFundId, setEditingFundId] = useState<number | undefined>(
    undefined
  );

  const funds = useAtomValue(FundAtom);

  const createNewFund = async (name: string) => {
    if (name.trim() === "") {
      toast.error("Invalid fund name", {
        description: "Fund name cannot be empty",
      });
      return;
    }

    const result = await addOrUpdateFund({
      name,
      fundKey: name,
    } as Partial<Fund>);
    if (!result) {
      toast.error("Error creating fund");
      return;
    }
    toast.success("Fund created", {
      description: `"${name}" has been added`,
    });
    props.onChange();
    setOpen(false);
  };

  const updateFund = async (fund: Fund, name: string) => {
    if (name.trim() === "") {
      toast.error("Invalid fund name", {
        description: "Fund name cannot be empty",
      });
      return;
    }

    const result = await addOrUpdateFund({ ...fund, name } as Partial<Fund>);
    if (!result) {
      toast.error("Error updating fund");
      return;
    }
    toast.success("Fund updated", {
      description: `"${name}" has been changed`,
    });
    setEditingFundId(undefined);
    props.onChange();
  };

  const deleteFund = async (fundId: number) => {
    const fund = funds.find(f => f.fundId === fundId);
    if (!fund) {
      toast.error("Fund not found");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete "${fund.name}" and all associated transactions?`
      )
    ) {
      await deleteFundById(fund.fundId);
      setEditingFundId(undefined);
      props.onChange();
      toast.success("Fund deleted", {
        description: `"${fund.name}" has been removed`,
      });
    }
  };

  return {
    open,
    setOpen,
    funds,
    createNewFund,
    editingFundId,
    setEditingFundId,
    updateFund,
    deleteFund,
  };
};
